import { use } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as LinkIcon } from '@mui/icons-material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import SystemDevice from 'features/systems/domain/models/system-device';
import IPUnitProviderContext from 'features/systems/ip-unit/providers/contexts/ip-unit-provider-context';
import styles from './ip-unit-wireless-devices.module.scss';

const IPUnitWirelessDevices = (): React.JSX.Element => {
  const { t } = useTranslation('ipUnit');
  const { t: tDeviceTypes } = useTranslation('deviceTypes');

  const { openDeviceDetailsByDeviceId, viewingMode, getWirelessDevices } = use(IPUnitProviderContext);

  const columns: ColumnDefinition<SystemDevice, NestedKeyof<SystemDevice>>[] = [
    {
      key: 'name',
      label: t('wirelessDevices.device'),
      disableSort: true,
      renderCustomContentProvider: (item) => getNameRender(item),
    },
    {
      key: 'type',
      label: t('wirelessDevices.type'),
      renderCustomContentProvider: (item) => <Typography variant="subtitle1">{tDeviceTypes(`${item.type}`)}</Typography>,
      disableSort: true,
    },
  ];

  const getNameRender = (item: SystemDevice): React.JSX.Element => (
    <Stack direction="row" className={styles.nameIconContainer}>
      <Typography
        className={`${styles.location} ${viewingMode === 'viewing' ? styles.clickable : ''} `}
        variant="subtitle1"
        onClick={() => openDeviceDetailsByDeviceId(item.id)}
      >
        {item.name}
      </Typography>
      {item.organisationUnitId && (
        <BreadCrumbTooltip allElementNames={item.breadCrumbs ?? []}>
          <LinkIcon className={styles.linkIcon} />
        </BreadCrumbTooltip>
      )}
    </Stack>
  );

  return getWirelessDevices().length > 0 ? (
    <div className={styles.container}>
      <Typography className={styles.title} variant="h5">
        {t('wirelessDevices.title')}
      </Typography>
      <Table
        className={styles.table}
        columns={columns}
        data={getWirelessDevices().map<RowItem<SystemDevice>>((linkableDevice) => ({ data: linkableDevice, showColorOnHover: false }))}
        rowIdentifier={(item) => item.id ?? ''}
      />
    </div>
  ) : (
    <></>
  );
};

export default IPUnitWirelessDevices;
